import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import i18n from 'i18next';
const inboundEmailMessagesAdapter = createEntityAdapter();
const outboundEmailMessagesAdapter = createEntityAdapter();
const contactsAdapter = createEntityAdapter();

export const projectEmailSlice = createSlice({
  name: 'projectEmail',
  initialState: {
    project: null,
    inboundEmailMessages: inboundEmailMessagesAdapter.getInitialState(),
    outboundEmailMessages: outboundEmailMessagesAdapter.getInitialState(),
    loadingInboundMessages: false,
    loadingOutboundMessages: false,
    loadingProject: false,
    loadingSettings: false,
    loading: false,
    error: null,
    allInboundMessagesFetched: false,
    allOutboundMessagesFetched: false,
    lastEvaluatedKeyInbound: null,
    lastEvaluatedKeyOutbound: null,
    initialLoadDone: false,
    mailbox: 'inbound',
    sendEmailStatus: {
      loading: false,
      error: null,
      success: false,
    },
    unreadMessages: {},
    emailMessagesCounts: {},
    settings: null,
    settingsOpen: false,
    contacts: contactsAdapter.getInitialState(),
    loadingContacts: false,
    notifyEmailMessage: null,
    threadMessages: {},
    loadingThread: false,
  },
  reducers: {
    switchMailbox(state, action) {
      state.mailbox = state.mailbox === 'inbound' ? 'outbound' : 'inbound';
    },
    setSettingsOpen(state, action) {
      state.settingsOpen = action.payload;
    },
    addEmailMessage(state, action) {
      const { emailMessage } = action.payload;
      if (emailMessage.direction === 'inbound') {
        inboundEmailMessagesAdapter.addOne(
          state.inboundEmailMessages,
          emailMessage
        );
        // Move the new message to the start of the list
        const ids = state.inboundEmailMessages.ids;
        const newId = emailMessage.id;
        state.inboundEmailMessages.ids = [
          newId,
          ...ids.filter((id) => id !== newId),
        ];
      } else if (emailMessage.direction === 'outbound') {
        outboundEmailMessagesAdapter.addOne(
          state.outboundEmailMessages,
          emailMessage
        );
        // Move the new message to the start of the list
        const ids = state.outboundEmailMessages.ids;
        const newId = emailMessage.id;
        state.outboundEmailMessages.ids = [
          newId,
          ...ids.filter((id) => id !== newId),
        ];
      }
    },
    addUnreadMessage(state, action) {
      const { projectKey } = action.payload;
      state.unreadMessages[projectKey] =
        (state.unreadMessages[projectKey] || 0) + 1;
      state.emailMessagesCounts[projectKey] =
        (state.emailMessagesCounts[projectKey] || 0) + 1;
    },
    setNotifyEmailMessage(state, action) {
      state.notifyEmailMessage = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase('projectEmail/setProject/pending', (state, action) => {
        const { projectKey } = action.meta.arg;
        if (state.project?.projectKey !== projectKey) {
          state.inboundEmailMessages =
            inboundEmailMessagesAdapter.getInitialState();
          state.outboundEmailMessages =
            outboundEmailMessagesAdapter.getInitialState();
          state.contacts = contactsAdapter.getInitialState();
          state.error = null;
          state.loadingProject = true;
          state.loading = true;
          state.project = null;
          state.allInboundMessagesFetched = false;
          state.allOutboundMessagesFetched = false;
          state.lastEvaluatedKeyInbound = null;
          state.lastEvaluatedKeyOutbound = null;
          state.initialLoadDone = false;
          state.settings = null;
        }
      })
      .addCase('projectEmail/setProject/fulfilled', (state, action) => {
        state.project = action.payload;
        state.loadingProject = false;
        state.loading = state.loadingInboundMessages;
        if (!state.initialLoadDone) {
          state.initialLoadDone = !state.loadingInboundMessages;
        }
      })
      .addCase('projectEmail/getOutboundEmailMessages/pending', (state) => {
        state.loadingOutboundMessages = true;
      })
      .addCase(
        'projectEmail/getOutboundEmailMessages/fulfilled',
        (state, action) => {
          state.loadingOutboundMessages = false;
          const { Items: emailMessages } = action.payload;
          outboundEmailMessagesAdapter.upsertMany(
            state.outboundEmailMessages,
            emailMessages
          );
          if (emailMessages.length > 0) {
            const lastMessage = emailMessages.at(-1);
            state.lastEvaluatedKeyOutbound = {
              id: lastMessage.id,
              GSI1PK: lastMessage.GSI1PK,
              GSI1SK: lastMessage.GSI1SK,
            };
          } else {
            state.allOutboundMessagesFetched = true;
          }
        }
      )
      .addCase(
        'projectEmail/getOutboundEmailMessages/rejected',
        (state, action) => {
          state.loadingOutboundMessages = false;
          state.error = true;
        }
      )
      .addCase('projectEmail/getInboundEmailMessages/pending', (state) => {
        state.loadingInboundMessages = true;
        state.loading = true;
        state.error = null;
      })
      .addCase(
        'projectEmail/getInboundEmailMessages/fulfilled',
        (state, action) => {
          state.loadingInboundMessages = false;
          state.loading = state.loadingProject;
          if (!state.initialLoadDone) {
            state.initialLoadDone = !state.loadingProject;
          }
          const { Items: emailMessages } = action.payload;
          inboundEmailMessagesAdapter.upsertMany(
            state.inboundEmailMessages,
            emailMessages
          );
          if (emailMessages.length > 0) {
            const lastMessage = emailMessages.at(-1);
            state.lastEvaluatedKeyInbound = {
              id: lastMessage.id,
              GSI1PK: lastMessage.GSI1PK,
              GSI1SK: lastMessage.GSI1SK,
            };
          } else {
            state.allInboundMessagesFetched = true;
          }
        }
      )
      .addCase(
        'projectEmail/getInboundEmailMessages/rejected',
        (state, action) => {
          state.loadingInboundMessages = false;
          state.loading = state.loadingProject;
          state.error = true;
          state.initialLoadDone = true;
        }
      )
      .addCase('projectEmail/markEmailRead/pending', (state, action) => {
        const { emailMessage } = action.meta.arg;
        if (emailMessage.direction === 'inbound') {
          inboundEmailMessagesAdapter.updateOne(state.inboundEmailMessages, {
            id: emailMessage.id,
            changes: { isRead: true, isReadByCurrentUser: true },
          });
          state.unreadMessages[emailMessage.projectKey] = Math.max(
            0,
            (state.unreadMessages[emailMessage.projectKey] || 0) - 1
          );
        } else if (emailMessage.direction === 'outbound') {
          outboundEmailMessagesAdapter.updateOne(state.outboundEmailMessages, {
            id: emailMessage.id,
            changes: { isRead: true, isReadByCurrentUser: true },
          });
        }
      })
      .addCase('projectEmail/setProject/rejected', (state, action) => {
        state.loadingProject = false;
        state.loading = state.loadingInboundMessages;
        state.error = true;
        state.initialLoadDone = true;
      })
      .addCase('projectEmail/reloadMessages/pending', (state) => {
        inboundEmailMessagesAdapter.removeAll(state.inboundEmailMessages);
        outboundEmailMessagesAdapter.removeAll(state.outboundEmailMessages);
        state.loadingInboundMessages = true;
        state.loadingOutboundMessages = true;
        state.loading = true;
        state.error = null;
        state.allInboundMessagesFetched = false;
        state.allOutboundMessagesFetched = false;
        state.lastEvaluatedKeyInbound = null;
        state.lastEvaluatedKeyOutbound = null;
        state.initialLoadDone = false;
      })
      .addCase('projectEmail/sendEmail/pending', (state) => {
        state.sendEmailStatus.loading = true;
        state.sendEmailStatus.error = null;
        state.sendEmailStatus.success = false;
      })
      .addCase('projectEmail/sendEmail/fulfilled', (state, action) => {
        const emailMessage = action.payload;
        projectEmailSlice.caseReducers.addEmailMessage(state, {
          payload: {
            emailMessage: { ...emailMessage, isReadByCurrentUser: true },
          },
        });
        state.sendEmailStatus.loading = false;
        state.sendEmailStatus.error = null;
        state.sendEmailStatus.success = true;
        enqueueSnackbar(i18n.t('projectEmail.email_sent'), {
          variant: 'success',
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        });
      })
      .addCase('projectEmail/sendEmail/rejected', (state, action) => {
        state.sendEmailStatus.loading = false;
        state.sendEmailStatus.error = action.error;
        state.sendEmailStatus.success = false;
        enqueueSnackbar(i18n.t('projectEmail.error_sending_email'), {
          variant: 'error',
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        });
      })
      .addCase(
        'projectEmail/getUnreadMessagesCount/fulfilled',
        (state, action) => {
          const payload = action.payload;
          if (!payload || typeof payload !== 'object') return;
          const entries = Object.entries(payload);
          state.unreadMessages = Object.fromEntries(
            entries.map(([key, val]) => [
              key,
              // Handle both new shape { unreadCount, messagesCount } and legacy plain number
              typeof val === 'object' && val !== null
                ? Math.max(0, Number(val.unreadCount) || 0)
                : Math.max(0, Number(val) || 0),
            ])
          );
          state.emailMessagesCounts = Object.fromEntries(
            entries.map(([key, val]) => [
              key,
              typeof val === 'object' && val !== null
                ? Math.max(0, Number(val.messagesCount) || 0)
                : 0,
            ])
          );
        }
      )
      .addCase('projectEmail/getEmailSettings/pending', (state) => {
        state.loadingSettings = true;
      })
      .addCase('projectEmail/getEmailSettings/rejected', (state) => {
        state.loadingSettings = false;
      })
      .addCase('projectEmail/getEmailSettings/fulfilled', (state, action) => {
        state.settings = action.payload;
        state.loadingSettings = false;
      })
      .addCase('projectEmail/saveEmailSettings/pending', (state) => {
        state.loadingSettings = true;
        state.settingsOpen = false;
      })
      .addCase('projectEmail/saveEmailSettings/fulfilled', (state, action) => {
        state.settings = { ...state.settings, ...action.payload };
        state.loadingSettings = false;
        enqueueSnackbar(i18n.t('projectEmail.settings_saved'), {
          variant: 'success',
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        });
      })
      .addCase('projectEmail/saveEmailSettings/rejected', (state, action) => {
        state.loadingSettings = false;
        enqueueSnackbar(i18n.t('projectEmail.error_saving_settings'), {
          variant: 'error',
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        });
      })
      .addCase('projectEmail/getContacts/pending', (state) => {
        state.loadingContacts = true;
      })
      .addCase('projectEmail/getContacts/rejected', (state) => {
        state.loadingContacts = false;
      })
      .addCase('projectEmail/getContacts/fulfilled', (state, action) => {
        const { Items: contacts } = action.payload;
        contactsAdapter.setAll(state.contacts, contacts);
        state.loadingContacts = false;
      })
      .addCase('projectEmail/getEmailThread/pending', (state) => {
        state.loadingThread = true;
      })
      .addCase('projectEmail/getEmailThread/fulfilled', (state, action) => {
        const { Items: messages } = action.payload;
        const { threadId } = action.meta.arg;
        state.threadMessages[threadId] = messages || [];
        state.loadingThread = false;
      })
      .addCase('projectEmail/getEmailThread/rejected', (state) => {
        state.loadingThread = false;
      });
  },
});

//ACTIONS
export const {
  switchMailbox,
  setSettingsOpen,
  addEmailMessage,
  addUnreadMessage,
  setNotifyEmailMessage,
} = projectEmailSlice.actions;

// SELECTORS
export const getProjectEmailData = (state) => {
  return state.projectEmailSlice;
};
